import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../components/Services.css';

const TeamsPage = () => {
  const [visibleTeams, setVisibleTeams] = useState([]);
  const [visibleExecutives, setVisibleExecutives] = useState([]);
  const teamRefs = useRef([]);
  const executiveRefs = useRef([]);
  const sectionRef = useRef(null);

  const coreTeams = [
    {
      title: 'Engineering & Innovation',
      description: 'Turning ideas into impact. Our engineers and designers build adaptive, engaging games that seniors can enjoy regardless of mobility, cognitive, or sensory challenges. By blending technology, creativity, and accessibility, we create solutions that make play inclusive for all. Our team designs, tests, and refines these games through hands-on collaboration with seniors to ensure they\'re fun, functional, and truly accessible.',
      video: '/videos/7475254-hd_1920_1080_25fps.mp4',
      link: '/teams/engineering'
    },
    {
      title: 'Volunteering & Community Engagement',
      description: 'Because change happens face-to-face. Our volunteers bring energy, empathy, and engagement straight to seniors through interactive game nights, social events, and meaningful one-on-one connections. We organize and host these experiences in senior homes, ensuring that every event fosters real relationships, combats isolation, and promotes health.',
      video: '/videos/6646663-hd_1080_1920_24fps.mp4',
      link: '/teams/volunteering'
    },
    {
      title: 'Research & Awareness',
      description: 'Shining a light on untold stories. We dive deep into the realities of aging, tackling issues like isolation, ageism, accessibility, and intergenerational divides. Our team runs a monthly newsletter featuring interviews, insights, and stories that highlight the experiences of older adults, turning knowledge into action. We also collaborate with the Engineering team to ensure that research findings help shape better, more accessible games.',
      video: '/videos/6646679-hd_1080_1920_24fps.mp4',
      link: '/teams/research'
    }
  ];

  // Generate 24 executive placeholders (4 columns x 6 rows)
  const executives = Array.from({ length: 24 }, (_, i) => ({
    id: i + 1,
    name: '[Executive Name]',
    position: '[Position]',
    bio: '[Short bio about the executive and their role in E-Q.O.L.]',
    image: `/images/executives/exec${i + 1}-placeholder.jpg`,
    linkedin: '#',
    instagram: '#'
  }));

  useEffect(() => {
    const teamObservers = teamRefs.current.map((ref, index) => {
      if (!ref) return null;
      
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleTeams(prev => [...prev, index]);
          }
        },
        { threshold: 0.1 }
      );
      
      observer.observe(ref);
      return observer;
    });

    const executiveObservers = executiveRefs.current.map((ref, index) => {
      if (!ref) return null;
      
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleExecutives(prev => [...prev, index]);
          }
        },
        { threshold: 0.1 }
      );
      
      observer.observe(ref);
      return observer;
    });

    return () => {
      teamObservers.forEach(observer => observer && observer.disconnect());
      executiveObservers.forEach(observer => observer && observer.disconnect());
    };
  }, []);

  return (
    <div className="page-container">
      {/* Executives & Office Holders Section */}
      <section className="executives-section section" ref={sectionRef}>
        <div className="container">
          <div className="services-header">
            <h2 className="section-title">Our Leadership Team</h2>
            <p className="section-subtitle">
              Meet the executives and office holders who lead E-Q.O.L. and drive our mission forward.
            </p>
          </div>

          {/* Group Photo */}
          <div className="executives-group-photo">
            <div className="group-photo-container">
              <img
                src="/images/group.jpeg"
                alt="E-Q.O.L. Executive Team Group Photo"
                className="group-photo-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                  const container = e.target.parentElement;
                  if (!container.querySelector('.group-photo-placeholder')) {
                    const placeholder = document.createElement('div');
                    placeholder.className = 'group-photo-placeholder';
                    placeholder.innerHTML = '<div class="placeholder-icon">👥</div><p>Upload group photo here</p>';
                    container.appendChild(placeholder);
                  }
                }}
              />
            </div>
          </div>

          {/* Individual Executives */}
          <div className="executives-grid">
            {executives.map((exec, index) => (
              <div
                key={exec.id}
                className={`executive-card ${visibleExecutives.includes(index) ? 'animate-fade-in' : ''}`}
                ref={el => executiveRefs.current[index] = el}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="executive-image-container">
                  <img
                    src={exec.image}
                    alt={exec.name}
                    className="executive-image"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const container = e.target.parentElement;
                      if (!container.querySelector('.executive-placeholder')) {
                        const placeholder = document.createElement('div');
                        placeholder.className = 'executive-placeholder';
                        placeholder.innerHTML = '<div class="placeholder-icon">👤</div>';
                        container.appendChild(placeholder);
                      }
                    }}
                  />
                </div>
                <div className="executive-content">
                  <h3 className="executive-name">{exec.name}</h3>
                  <p className="executive-position">{exec.position}</p>
                  <p className="executive-bio">{exec.bio}</p>
                  <div className="executive-social">
                    {exec.linkedin !== '#' && (
                      <a href={exec.linkedin} target="_blank" rel="noopener noreferrer" className="social-icon-link">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                        </svg>
                      </a>
                    )}
                    {exec.instagram !== '#' && (
                      <a href={exec.instagram} target="_blank" rel="noopener noreferrer" className="social-icon-link">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                        </svg>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Teams Section */}
      <section className="services section">
        <div className="container">
          <div className="services-header">
            <h2 className="section-title">We're not waiting for change—we're creating it.</h2>
            <p className="section-subtitle">
              Our club is built on three core teams, each working toward a more inclusive future in unique ways.
            </p>
          </div>
          
          <div className="core-teams-grid">
            {coreTeams.map((team, index) => (
              <Link 
                key={index}
                to={team.link}
                className={`team-card team-card-link elevated-team-card ${visibleTeams.includes(index) ? 'animate-fade-in' : ''}`}
                ref={el => teamRefs.current[index] = el}
              >
                <div className="team-video">
                  <video 
                    src={team.video} 
                    className="team-bg-video"
                    autoPlay
                    muted
                    loop
                    playsInline
                  />
                </div>
                <div className="team-content">
                  <h3 className="team-title">{team.title}</h3>
                  <p className="team-description">{team.description}</p>
                  <div className="team-link-arrow">
                    Learn More →
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default TeamsPage;

